/**
 * What an action says while it is running, and what it says when it fails.
 *
 * DESIGN.md section 6: a failed action shows the server's own message and the last six
 * characters of the request id, and the form stays filled. Spec 02-orders adds that the
 * cancel refusal is shown verbatim, because "Void the label in ShipStation first" is the
 * instruction, and paraphrasing it into "could not cancel" is how an owner ends up guessing.
 *
 * So there is no generic toast on this screen. Every failure renders next to the control
 * that caused it, with the problem+json `detail`, its field errors and its code.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { ApiProblem } from '@/lib/api';

export interface ActionState {
  busy: boolean;
  problem: ApiProblem | null;
  error: string | null;
  /** Runs the action, keeps the failure, and reports whether it worked. */
  run: (action: () => Promise<unknown>) => Promise<boolean>;
  reset: () => void;
}

export function useAction(): ActionState {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setProblem(null);
    setError(null);
  }, []);

  const run = useCallback(async (action: () => Promise<unknown>) => {
    setBusy(true);
    setProblem(null);
    setError(null);
    try {
      await action();
      return true;
    } catch (caught) {
      if (caught instanceof ApiProblem) setProblem(caught);
      else setError(caught instanceof Error ? caught.message : 'The action did not run.');
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, problem, error, run, reset };
}

/** The link an operator needs when the answer is "go and void the label". */
const SHIPSTATION_ORDERS = 'https://ship.shipstation.com/orders';

export function ActionProblem({ state }: { state: ActionState }) {
  if (!state.problem && !state.error) return null;

  if (!state.problem) {
    return (
      <p className="ord-problem" role="alert">
        {state.error}
      </p>
    );
  }

  const problem = state.problem;
  const fields = problem.problem?.errors ?? [];
  const label = problem.code === 'order.label_active';

  return (
    <div className="ord-problem" role="alert">
      <p className="ord-problem__detail">{problem.message}</p>
      {fields.length > 0 ? (
        <ul className="ord-problem__fields">
          {fields.map((field) => (
            <li key={`${field.field}-${field.code}`}>
              <span className="o-mono">{field.field}</span> {field.message}
            </li>
          ))}
        </ul>
      ) : null}
      {label ? (
        <p>
          <a href={SHIPSTATION_ORDERS} target="_blank" rel="noreferrer noopener">
            Open ShipStation
          </a>
        </p>
      ) : null}
      <p className="ord-problem__meta">
        <span className="o-mono">{problem.code}</span>
        {problem.shortRequestId ? (
          <span className="o-mono"> request {problem.shortRequestId}</span>
        ) : null}
      </p>
    </div>
  );
}

/** A form that opens under the action row. Escape closes it, the caller owns the state. */
export function ActionPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <section
      className="ord-panel"
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <h4 className="o-section-title">{title}</h4>
      {children}
    </section>
  );
}
